// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 03-mvp-scope.md#integration-marketplace--provider-profile-catalog
import { AXIOM_API_TOKEN_STEPS } from './axiom-api-token-guide.js';
import { BOX_AGENT_INSTRUCTIONS, BOX_CCG_USER_STEPS } from './box-ccg-user-guide.js';
import { GITHUB_APP_STEPS } from './github-app-guide.js';
import { GOOGLE_WORKSPACE_SERVICE_ACCOUNT_STEPS } from './google-workspace-service-account-guide.js';
import { FABRIC_WAREHOUSE_SERVICE_PRINCIPAL_STEPS, MICROSOFT_FABRIC_SERVICE_PRINCIPAL_STEPS, } from './microsoft-fabric-service-principal-guide.js';
import { FABRIC_WAREHOUSE_USER_AGENT_INSTRUCTIONS, FABRIC_WAREHOUSE_USER_STEPS, MICROSOFT_FABRIC_AGENT_INSTRUCTIONS, MICROSOFT_FABRIC_USER_STEPS, POWER_BI_SEMANTIC_MODELS_AGENT_INSTRUCTIONS, POWER_BI_SEMANTIC_MODELS_STEPS, } from './microsoft-fabric-user-guide.js';
import { MICROSOFT_GRAPH_AGENT_INSTRUCTIONS, MICROSOFT_GRAPH_USER_STEPS, } from './microsoft-graph-user-guide.js';
import { MICROSOFT_GRAPH_APP_ONLY_AGENT_INSTRUCTIONS, MICROSOFT_GRAPH_APP_ONLY_STEPS, } from './microsoft-graph-app-only-guide.js';
import { MICROSOFT_TEAMS_CLIENT_CREDENTIALS_STEPS } from './microsoft-teams-client-credentials-guide.js';
import { NETSUITE_M2M_STEPS } from './netsuite-m2m-guide.js';
import { formatAcquisitionGuideSteps, SALESFORCE_CLIENT_CREDENTIALS_STEPS, } from './salesforce-client-credentials-guide.js';
import { SNOWFLAKE_AGENT_INSTRUCTIONS, SNOWFLAKE_BLOCKED_ROLES, SNOWFLAKE_SECURITY_INTEGRATION_STATEMENT, SNOWFLAKE_USER_STEPS, } from './snowflake-user-guide.js';
import { ENTRA_SIGN_IN_TEST_HINTS, SNOWFLAKE_SIGN_IN_TEST_HINTS } from './sign-in-test-hints.js';
import { USCIS_CASE_STATUS_STEPS } from './uscis-case-status-guide.js';
// The MVP integration-catalog slice (ADR-0026, docs/03 § Integration Marketplace).
// Code-reviewed, version-pinned data shipped with the platform release — not a DB
// table, not customer-editable. Each entry's `catalogKey` is stamped onto an
// integration's `catalog_key` at registration; the Custom path is the absence of an
// entry (`catalog_key = null`). Adding/changing an entry is a reviewed code change.
//
// Invariants enforced by catalog.test.ts (AC-CAT-04): unique catalogKey, exactly one
// default profile per entry, and a non-proxyable entry offers no proxied delivery.
//
// `auth_inject` placements mirror docs/09 § Tier 1. No secrets live here — only base
// URLs, wire shapes, credential kinds, and public docs links.
export const CATALOG_ENTRIES = [
    {
        catalogKey: 'openai',
        displayName: 'OpenAI',
        category: 'ai',
        baseUrl: 'https://api.openai.com/v1',
        probePath: '/models',
        logoRef: 'openai',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://platform.openai.com/api-keys',
                acquisitionInstructions: 'Create a secret API key in the OpenAI dashboard (Settings → API keys) for a service account, then paste it here.',
                secretPlaceholder: 'sk-…',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'anthropic',
        displayName: 'Anthropic',
        category: 'ai',
        // Host root, no /v1 — the Anthropic SDKs append /v1/... to ANTHROPIC_BASE_URL
        // themselves (unlike OpenAI's, whose default base URL includes /v1).
        baseUrl: 'https://api.anthropic.com',
        logoRef: 'anthropic',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                // Anthropic API keys go in the x-api-key header, not a Bearer token. The
                // required anthropic-version header is sent by the calling app (the SDKs
                // add it), so it is not part of credential injection.
                authInject: { kind: 'custom_header', header_name: 'x-api-key' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://platform.claude.com/settings/keys',
                acquisitionInstructions: 'Create an API key in the Anthropic Console (Settings → API keys) — prefer a dedicated workspace so spend and rate limits are scoped to this integration — and paste it here.',
                secretPlaceholder: 'sk-ant-…',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'github',
        displayName: 'GitHub',
        category: 'developer',
        baseUrl: 'https://api.github.com',
        placeholders: [
            {
                token: 'installation_id',
                label: 'Installation ID',
                hint: 'The number at the end of the App installation URL (…/installations/<id>).',
                example: '12345678',
                format: 'digits',
            },
        ],
        logoRef: 'github',
        proxyable: true,
        connectionProfiles: [
            {
                // Org-owned GitHub App installation — GitHub's one programmatic credential
                // tied to an installation, not a user. Greenlight mints 1-hour ghs_ tokens
                // from the vaulted App key (docs/09 §Tier 2, method github_app). NOT the
                // Greenlight source-control App: data access stays independent of SCM.
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'github_app',
                tokenUrl: 'https://api.github.com/app/installations/{installation_id}/access_tokens',
                tokenEndpointAuthMethod: 'github_app',
                displayName: 'GitHub App (org-owned installation)',
                clientIdLabel: 'App ID',
                clientIdPlaceholder: 'Numeric App ID from the GitHub App settings page',
                setupGuide: {
                    docsLabel: 'GitHub App installation auth docs',
                    stepsTitle: 'GitHub App setup guide',
                    steps: GITHUB_APP_STEPS,
                },
                acquisitionDocsUrl: 'https://docs.github.com/en/apps/creating-github-apps/authenticating-with-a-github-app/authenticating-as-a-github-app-installation',
                acquisitionInstructions: formatAcquisitionGuideSteps(GITHUB_APP_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
            {
                id: 'service_account',
                authMode: 'service_account',
                // A PAT rides as a bearer token, but GitHub ties every PAT — classic or
                // fine-grained — to a person's account: it consumes a seat, often needs
                // per-token SSO authorization, and dies when that person leaves.
                authInject: { kind: 'bearer' },
                credentialKind: 'personal_access_token',
                acquisitionDocsUrl: 'https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens',
                acquisitionInstructions: 'Escape hatch only — a personal access token belongs to a GitHub user account: it consumes a seat, may need per-token SSO authorization, and stops working when that person leaves. Prefer the GitHub App profile. If you must: create a fine-grained PAT (Settings → Developer settings → Personal access tokens) scoped to only the repositories and permissions this integration needs, and paste it here. Independent of the Greenlight source-control App.',
                secretPlaceholder: 'ghp_… or github_pat_…',
                isDefault: false,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'slack',
        displayName: 'Slack',
        category: 'communication',
        baseUrl: 'https://slack.com/api',
        probePath: '/auth.test',
        logoRef: 'slack',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'bot_token',
                acquisitionDocsUrl: 'https://api.slack.com/authentication/token-types#bot',
                acquisitionInstructions: 'Install the Greenlight Slack app to your workspace and approve the bot scopes; paste the resulting bot token (xoxb-…).',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'microsoft-teams',
        displayName: 'Microsoft Teams',
        category: 'communication',
        baseUrl: 'https://graph.microsoft.com',
        searchKeywords: ['Graph', 'Chat', 'Channels', 'Microsoft 365'],
        placeholders: [
            {
                token: 'tenant_id',
                label: 'Directory (tenant) ID',
                hint: 'The Entra tenant the dedicated app is registered in.',
                example: '00000000-0000-0000-0000-000000000000',
            },
        ],
        logoRef: 'microsoft-teams',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                // Per-tenant Entra token endpoint — IT substitutes {tenant_id} at registration.
                tokenUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token',
                scopes: ['https://graph.microsoft.com/.default'],
                setupGuide: {
                    docsLabel: 'Teams RSC docs',
                    stepsTitle: 'Microsoft Teams setup guide',
                    steps: MICROSOFT_TEAMS_CLIENT_CREDENTIALS_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/microsoftteams/platform/graph-api/rsc/resource-specific-consent',
                acquisitionInstructions: formatAcquisitionGuideSteps(MICROSOFT_TEAMS_CLIENT_CREDENTIALS_STEPS),
                clientIdPlaceholder: 'Application (client) ID from the Entra app',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'microsoft-graph',
        displayName: 'Microsoft Graph',
        category: 'productivity',
        baseUrl: 'https://graph.microsoft.com',
        searchKeywords: [
            'Outlook',
            'Mail',
            'Email',
            'Calendar',
            'OneDrive',
            'SharePoint',
            'Teams',
            'Microsoft 365',
        ],
        placeholders: [
            {
                token: 'tenant_id',
                label: 'Directory (tenant) ID',
                hint: 'The GUID of the Entra tenant the registration lives in, never a domain name or /common: the connect callback compares it with each ID token.',
                example: '00000000-0000-0000-0000-000000000000',
                format: 'uuid',
            },
        ],
        logoRef: 'microsoft-graph',
        proxyable: true,
        connectionProfiles: [
            {
                // Per-user OAuth (docs/48 §Microsoft Graph): each credential is one Entra app
                // registration holding one purpose's delegated permissions; `.default` asks
                // for everything consented on it, which is also everything Entra would issue.
                id: 'user_delegated',
                authMode: 'user_delegated',
                mechanism: 'authorization_code',
                credentialKind: 'oauth_client',
                displayName: 'Per-user OAuth',
                authorizeUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/authorize',
                signInTestHints: ENTRA_SIGN_IN_TEST_HINTS,
                tokenUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token',
                scopes: ['openid', 'profile', 'offline_access', 'https://graph.microsoft.com/.default'],
                clientIdLabel: 'Application (client) ID',
                clientIdPlaceholder: 'Application (client) ID of this purpose’s registration',
                setupGuide: {
                    docsLabel: 'Graph delegated access docs',
                    stepsTitle: 'Microsoft Graph setup guide',
                    steps: MICROSOFT_GRAPH_USER_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/graph/auth-v2-user',
                agentInstructions: MICROSOFT_GRAPH_AGENT_INSTRUCTIONS,
                acquisitionInstructions: formatAcquisitionGuideSteps(MICROSOFT_GRAPH_USER_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
            {
                // App-only mail from named mailboxes (docs/49 track 4): registered as its own
                // integration, confined by Exchange RBAC for Applications, never per user.
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                displayName: 'Application permissions (app-only)',
                tokenUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token',
                scopes: ['https://graph.microsoft.com/.default'],
                clientIdPlaceholder: 'Application (client) ID of the system-mail registration',
                setupGuide: {
                    docsLabel: 'Exchange RBAC for Applications docs',
                    stepsTitle: 'Microsoft Graph app-only mail setup guide',
                    steps: MICROSOFT_GRAPH_APP_ONLY_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/exchange/permissions-exo/application-rbac',
                agentInstructions: MICROSOFT_GRAPH_APP_ONLY_AGENT_INSTRUCTIONS,
                acquisitionInstructions: formatAcquisitionGuideSteps(MICROSOFT_GRAPH_APP_ONLY_STEPS),
                isDefault: false,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'close',
        displayName: 'Close',
        category: 'crm',
        baseUrl: 'https://api.close.com/api/v1',
        probePath: '/me/',
        logoRef: 'close',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                // Close uses HTTP Basic with the API key as the username and an empty password.
                authInject: { kind: 'basic', username_from: 'secret' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://developer.close.com/topics/authentication/',
                acquisitionInstructions: 'Generate an API key for a designated Close service user (Settings → API Keys) and paste it here.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'hubspot',
        displayName: 'HubSpot',
        category: 'crm',
        baseUrl: 'https://api.hubapi.com',
        probePath: '/account-info/v3/details',
        logoRef: 'hubspot',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://developers.hubspot.com/docs/apps/developer-platform/build-apps/authentication/account-service-keys',
                acquisitionInstructions: 'Create a Service Key in HubSpot (Settings → Integrations → Service Keys), grant it only the scopes this integration needs, and paste it here. A legacy private-app access token works the same way — both are sent as bearer tokens.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'fireflies',
        displayName: 'Fireflies',
        category: 'meetings',
        baseUrl: 'https://api.fireflies.ai/graphql',
        logoRef: 'fireflies',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://docs.fireflies.ai/getting-started/authorization',
                acquisitionInstructions: 'A designated Fireflies service user generates an API key in Settings → Developer; paste it here.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'financial-modeling-prep',
        displayName: 'Financial Modeling Prep',
        category: 'market-data',
        baseUrl: 'https://financialmodelingprep.com/api/v3',
        logoRef: 'financial-modeling-prep',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'query_param', param_name: 'apikey' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://site.financialmodelingprep.com/developer/docs',
                acquisitionInstructions: 'Create an API key in the Financial Modeling Prep dashboard and paste it here; it is sent as the `apikey` query parameter.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'alpha-vantage',
        displayName: 'Alpha Vantage',
        category: 'market-data',
        baseUrl: 'https://www.alphavantage.co',
        logoRef: 'alpha-vantage',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'query_param', param_name: 'apikey' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://www.alphavantage.co/support/#api-key',
                acquisitionInstructions: 'Claim a free API key from Alpha Vantage and paste it here; it is sent as the `apikey` query parameter.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'fred',
        displayName: 'FRED (St. Louis Fed)',
        category: 'market-data',
        baseUrl: 'https://api.stlouisfed.org/fred',
        logoRef: 'fred',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'query_param', param_name: 'api_key' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://fredaccount.stlouisfed.org/apikeys',
                acquisitionInstructions: 'Request an API key from your FRED account and paste it here; it is sent as the `api_key` query parameter.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'earningscall',
        displayName: 'EarningsCall',
        category: 'market-data',
        baseUrl: 'https://v2.api.earningscall.biz',
        logoRef: 'earningscall',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'query_param', param_name: 'apikey' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://earningscall.biz/api-guide',
                acquisitionInstructions: 'Create an API key in your EarningsCall account and paste it here; it is sent as the `apikey` query parameter.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'sec-edgar',
        displayName: 'SEC EDGAR',
        category: 'market-data',
        baseUrl: 'https://data.sec.gov',
        logoRef: 'sec-edgar',
        proxyable: true,
        connectionProfiles: [
            {
                // Public data; no credential. SEC asks callers to send a descriptive
                // User-Agent, which the registration UI (T-6636) collects — there is no secret
                // to vault, so the profile carries no auth_inject.
                id: 'service_account',
                authMode: 'service_account',
                credentialKind: 'none',
                acquisitionDocsUrl: 'https://www.sec.gov/os/webmaster-faq#developers',
                acquisitionInstructions: 'No credential required — SEC EDGAR is public. Provide a descriptive User-Agent (company name + contact email) per SEC fair-access policy.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'zoom',
        displayName: 'Zoom',
        category: 'meetings',
        baseUrl: 'https://api.zoom.us/v2',
        logoRef: 'zoom',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                // Zoom Server-to-Server OAuth: account-credentials grant, no request scope.
                tokenUrl: 'https://zoom.us/oauth/token',
                acquisitionDocsUrl: 'https://developers.zoom.us/docs/internal-apps/s2s-oauth/',
                acquisitionInstructions: 'Create a Server-to-Server OAuth app in the Zoom Marketplace. Enter its client ID in Client ID on this form and paste only the client secret under Credentials; Greenlight mints the bearer token.',
                clientIdPlaceholder: 'Client ID from the Server-to-Server OAuth app',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'google-workspace',
        displayName: 'Google Workspace',
        category: 'productivity',
        baseUrl: 'https://www.googleapis.com',
        logoRef: 'google-workspace',
        proxyable: true,
        connectionProfiles: [
            {
                // A service-account JSON key, optionally with Domain-Wide Delegation. The
                // per-user DWD JWT mint is proxy logic (docs/09 § Google Workspace DWD),
                // not a static header — so no auth_inject; registration captures the
                // OAuth scopes the mint requests in auth_config.scopes.
                id: 'service_account',
                authMode: 'service_account',
                credentialKind: 'service_account_json',
                collectMintScopes: true,
                setupGuide: {
                    docsLabel: 'Service account credentials docs',
                    stepsTitle: 'Google Workspace setup guide',
                    steps: GOOGLE_WORKSPACE_SERVICE_ACCOUNT_STEPS,
                },
                acquisitionDocsUrl: 'https://cloud.google.com/iam/docs/service-account-creds',
                acquisitionInstructions: formatAcquisitionGuideSteps(GOOGLE_WORKSPACE_SERVICE_ACCOUNT_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'asana',
        displayName: 'Asana',
        category: 'productivity',
        baseUrl: 'https://app.asana.com/api/1.0',
        probePath: '/users/me',
        logoRef: 'asana',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'personal_access_token',
                acquisitionDocsUrl: 'https://developers.asana.com/docs/personal-access-token',
                acquisitionInstructions: 'Create a personal access token in the Asana developer console (app.asana.com/0/my-apps) under a designated service user — Asana Enterprise can mint a service-account token instead — and paste it here.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'notion',
        displayName: 'Notion',
        category: 'productivity',
        // Host root, no /v1 — the official SDK appends /v1/... itself.
        baseUrl: 'https://api.notion.com',
        logoRef: 'notion',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://developers.notion.com/guides/get-started/internal-connections',
                acquisitionInstructions: 'A workspace owner creates an internal connection in Settings → Connections, selects only the capabilities it needs, and pastes its installation access token here. Explicitly connect every page or database it should access; otherwise calls return empty or not-found results. Calling apps must send a supported `Notion-Version` header (the official SDK supplies its default).',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'rippling',
        displayName: 'Rippling',
        category: 'hr',
        baseUrl: 'https://rest.ripplingapis.com',
        logoRef: 'rippling',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://developer.rippling.com/documentation/rest-api/essentials/quickstart',
                acquisitionInstructions: 'In Rippling, open the Developer app (Tools → Developer), select API Tokens, and create a token from a durable admin or service user so owner offboarding does not revoke it. Effective access is the creator’s permissions intersected with the selected scopes, so use least-privilege read scopes. Paste the token when shown—it appears only once—and avoid leaving it idle for 30 days, which revokes it. Confirm API availability with your Rippling account manager.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'bigtime',
        displayName: 'BigTime',
        category: 'productivity',
        baseUrl: 'https://iq.bigtime.net/BigtimeData/api/v2',
        logoRef: 'bigtime',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: {
                    kind: 'multi_header',
                    headers: [
                        { header_name: 'X-Auth-ApiToken', value_from: 'secret' },
                        { header_name: 'X-Auth-Realm', value_from: 'realm' },
                    ],
                },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://iq.bigtime.net/BigtimeData/api/v2/Help/Overview',
                acquisitionInstructions: 'Have a system admin create a permanent firm access token using a least-privilege admin because the token mimics its creator’s rights. Enter the firm id as the non-secret realm value and paste the token as the credential. BigTime also documents a possibly required one-time POST /session/firm activation; verify it during the first real registration. The API allows 30 calls per minute per token and is available on Premier plans and above.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'lattice',
        displayName: 'Lattice',
        category: 'hr',
        baseUrl: 'https://api.latticehq.com',
        logoRef: 'lattice',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://developers.lattice.com/docs/authentication-1',
                acquisitionInstructions: 'In Lattice, open Admin → Settings → Platform → API keys and create the key from a dedicated admin service account because it inherits all privileges of its creator. Copy it when shown; keys are revoke-only and are not shown again. EMEA-residency customers should replace the base URL with https://api.emea.latticehq.com. This API covers the Lattice Talent suite only, not Lattice HRIS; confirm API availability with Lattice.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'greenhouse',
        displayName: 'Greenhouse Harvest',
        category: 'hr',
        baseUrl: 'https://harvest.greenhouse.io/v3',
        logoRef: 'greenhouse',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                tokenUrl: 'https://auth.greenhouse.io/token',
                tokenEndpointAuthMethod: 'client_secret_basic',
                acquisitionDocsUrl: 'https://harvestdocs.greenhouse.io/docs/authentication',
                acquisitionInstructions: 'In Configure → Dev Center → API Credential Management, create a Harvest V3 (OAuth) credential and select only the scopes this integration needs. Enter the client ID in Client ID and paste the client secret under Credentials; the secret is shown once. Rotation is a hard cutover that immediately invalidates the old secret. Do not create a legacy Harvest v1/v2 Basic key—those APIs reach EOL 2026-08-31.',
                clientIdPlaceholder: 'Harvest V3 OAuth client ID',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'uscis-case-status',
        displayName: 'USCIS Case Status',
        category: 'hr',
        // Sandbox and production are the same API on different hosts, and the OAuth
        // endpoint sits on whichever host is registered — so one placeholder fills both
        // URLs and moving to production is editing a field, not re-registering.
        baseUrl: 'https://{uscis_host}',
        placeholders: [
            {
                token: 'uscis_host',
                label: 'USCIS API host',
                hint: 'api-int.uscis.gov for the sandbox; api.uscis.gov once USCIS grants the app production access.',
                example: 'api.uscis.gov',
            },
        ],
        logoRef: 'uscis-case-status',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                tokenUrl: 'https://{uscis_host}/oauth/accesstoken',
                setupGuide: {
                    docsLabel: 'USCIS Case Status API docs',
                    stepsTitle: 'USCIS Case Status setup guide',
                    steps: USCIS_CASE_STATUS_STEPS,
                },
                acquisitionDocsUrl: 'https://developer.uscis.gov/api/case-status',
                acquisitionInstructions: formatAcquisitionGuideSteps(USCIS_CASE_STATUS_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'box',
        displayName: 'Box',
        category: 'documents',
        baseUrl: 'https://api.box.com',
        logoRef: 'box',
        proxyable: true,
        connectionProfiles: [
            {
                // The proxy resolves each signed-in user to their Box user id and mints a
                // Client Credentials Grant token with that user as the subject (docs/48 §Box).
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                displayName: 'Client Credentials Grant (as the user)',
                tokenUrl: 'https://api.box.com/oauth2/token',
                actsAsSignedInUser: true,
                authConfigFields: [
                    {
                        key: 'enterprise_id',
                        label: 'Enterprise ID',
                        hint: 'From the Box app’s General Settings tab in the Developer Console.',
                        example: '1234567',
                        format: 'digits',
                    },
                ],
                clientIdLabel: 'Client ID',
                clientIdPlaceholder: 'Box app Client ID',
                secretPlaceholder: 'Box app Client Secret',
                setupGuide: {
                    docsLabel: 'Client Credentials Grant docs',
                    stepsTitle: 'Box setup guide',
                    steps: BOX_CCG_USER_STEPS,
                },
                acquisitionDocsUrl: 'https://developer.box.com/guides/authentication/client-credentials/',
                agentInstructions: BOX_AGENT_INSTRUCTIONS,
                acquisitionInstructions: formatAcquisitionGuideSteps(BOX_CCG_USER_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'docusign',
        displayName: 'DocuSign',
        category: 'documents',
        baseUrl: 'https://{server}.docusign.net/restapi',
        placeholders: [
            {
                token: 'server',
                label: 'Server',
                hint: 'The account Base URI host prefix — for example na1 or demo.',
                example: 'na1',
            },
        ],
        logoRef: 'docusign',
        proxyable: false,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                credentialKind: 'service_account_json',
                acquisitionDocsUrl: 'https://developers.docusign.com/platform/auth/jwt/',
                acquisitionInstructions: 'Create a DocuSign integration key, generate an RSA keypair, and grant one-time admin consent for the signature impersonation scopes as a designated service user. Enter the account Base URI above and paste JSON shaped as {integration_key, user_id, rsa_private_key}. Apps use the DocuSign SDK JWT Grant helper to mint roughly one-hour access tokens and may discover the Base URI through /oauth/userinfo. Production use requires DocuSign go-live review. Delivery is injected because DocuSign offers no static credential or client-credentials grant; the app must hold the keypair and mint its own tokens.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'dealcloud',
        displayName: 'DealCloud',
        category: 'crm',
        // Per-customer host — single-tenant install, so the {site} placeholder is just
        // the base_url IT enters at registration (ADR-0026 note).
        baseUrl: 'https://{site}.dealcloud.com/api',
        placeholders: [
            {
                token: 'site',
                label: 'Site',
                hint: 'Your DealCloud site subdomain.',
                example: 'acme',
            },
        ],
        logoRef: 'dealcloud',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                // Per-customer host — the {site} placeholder is prompted for at registration.
                tokenUrl: 'https://{site}.dealcloud.com/api/rest/v1/oauth/token',
                acquisitionDocsUrl: 'https://help.dealcloud.com/',
                acquisitionInstructions: 'In DealCloud admin, create an API client. Enter its client ID in Client ID on this form and paste only the client secret under Credentials; Greenlight mints the bearer token against your DealCloud OAuth token endpoint.',
                clientIdPlaceholder: 'API client ID from DealCloud admin',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'apollo',
        displayName: 'Apollo',
        category: 'crm',
        baseUrl: 'https://api.apollo.io/v1',
        logoRef: 'apollo',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'custom_header', header_name: 'x-api-key' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://docs.apollo.io/docs/api-overview',
                acquisitionInstructions: 'Create an Apollo API key with only the access this integration needs and paste it here. Prefer a scoped key; use a master key only for endpoints that require it. Apollo sends the account-level key in the x-api-key header, so confirm its blast radius and available API credits match the intended workflows.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'jira',
        displayName: 'Jira Cloud',
        category: 'productivity',
        baseUrl: 'https://{site}.atlassian.net/rest/api/3',
        placeholders: [
            {
                token: 'site',
                label: 'Site',
                hint: 'Your Atlassian site subdomain.',
                example: 'acme',
            },
        ],
        logoRef: 'jira',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'basic', username_from: 'username' },
                credentialKind: 'api_key',
                acquisitionDocsUrl: 'https://developer.atlassian.com/cloud/jira/platform/basic-auth-for-rest-apis/',
                acquisitionInstructions: 'Create an API token for a dedicated Atlassian service account. Enter the account email as the Basic auth username and paste the API token as the secret; plan rotation before the token expires (maximum one year).',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'salesforce',
        displayName: 'Salesforce',
        category: 'crm',
        baseUrl: 'https://{my_domain}.my.salesforce.com',
        probePath: '/services/data/v62.0/',
        placeholders: [
            {
                token: 'my_domain',
                label: 'My Domain',
                hint: 'The My Domain prefix from Setup → Company Settings → My Domain.',
                example: 'acme',
                normalize: 'salesforce_my_domain',
                format: 'salesforce_my_domain',
            },
        ],
        logoRef: 'salesforce',
        proxyable: true,
        connectionProfiles: [
            {
                // Service identity through an External Client App client-credentials grant.
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                // Per-customer My Domain — the {my_domain} placeholder is prompted for.
                tokenUrl: 'https://{my_domain}.my.salesforce.com/services/oauth2/token',
                clientIdLabel: 'Consumer Key (client ID)',
                clientIdPlaceholder: 'Consumer Key from Manage Consumer Details',
                setupGuide: {
                    docsLabel: 'Client Credentials Flow Docs',
                    stepsTitle: 'Salesforce setup guide',
                    steps: SALESFORCE_CLIENT_CREDENTIALS_STEPS,
                },
                acquisitionDocsUrl: 'https://help.salesforce.com/s/articleView?id=sf.configure_client_credentials_flow_for_external_client_apps.htm&language=en_US&type=5',
                acquisitionInstructions: formatAcquisitionGuideSteps(SALESFORCE_CLIENT_CREDENTIALS_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
            {
                // User-delegated token exchange (Tier 3) — catalogued but deferred (ADR-0026 §3).
                // Registered as its own single-mode integration (e.g. `salesforce-user`).
                id: 'user_delegated',
                authMode: 'user_delegated',
                credentialKind: 'oauth_token_exchange',
                acquisitionDocsUrl: 'https://help.salesforce.com/s/articleView?id=sf.remoteaccess_oauth_token_exchange_flow.htm',
                acquisitionInstructions: 'Post-MVP. Configure an External Client App and a token-exchange handler so Salesforce trusts your IdP; Greenlight exchanges the signed-in user’s token per request.',
                isDefault: false,
                mvpSelectable: false,
            },
        ],
    },
    {
        catalogKey: 'netsuite',
        displayName: 'NetSuite',
        category: 'erp',
        // Account-specific SuiteTalk domain; records live under /record/v1 and SuiteQL
        // under /query/v1. A sandbox account ID like 123456_SB1 becomes 123456-sb1.
        baseUrl: 'https://{account_id}.suitetalk.api.netsuite.com/services/rest',
        placeholders: [
            {
                token: 'account_id',
                label: 'Account ID',
                hint: 'A sandbox account ID like 123456_SB1 becomes 123456-sb1 in the host.',
                example: '123456_SB1',
                normalize: 'lowercase_hyphenate',
            },
        ],
        logoRef: 'netsuite',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                tokenUrl: 'https://{account_id}.suitetalk.api.netsuite.com/services/rest/auth/oauth2/v1/token',
                tokenEndpointAuthMethod: 'private_key_jwt',
                scopes: ['rest_webservices'],
                displayName: 'NetSuite certificate (M2M)',
                setupGuide: {
                    docsLabel: 'NetSuite M2M setup docs',
                    stepsTitle: 'NetSuite setup guide',
                    steps: NETSUITE_M2M_STEPS,
                },
                acquisitionDocsUrl: 'https://docs.oracle.com/en/cloud/saas/netsuite/ns-online-help/section_162686838198.html',
                acquisitionInstructions: formatAcquisitionGuideSteps(NETSUITE_M2M_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
            {
                // User-delegated authorization-code grant (Tier 3) — catalogued but
                // deferred (ADR-0026 §3).
                id: 'user_delegated',
                authMode: 'user_delegated',
                credentialKind: 'oauth_token_exchange',
                acquisitionDocsUrl: 'https://docs.oracle.com/en/cloud/saas/netsuite/ns-online-help/section_157780312610.html',
                acquisitionInstructions: 'Post-MVP. Enable the Authorization Code Grant on the integration record; Greenlight would drive the user consent flow and refresh tokens per signed-in user.',
                isDefault: false,
                mvpSelectable: false,
            },
        ],
    },
    {
        catalogKey: 'snowflake',
        displayName: 'Snowflake',
        category: 'data-warehouse',
        baseUrl: 'https://{account}.snowflakecomputing.com',
        placeholders: [
            {
                token: 'account',
                label: 'Account identifier',
                hint: 'Your <orgname>-<account_name>, from Admin → Accounts (not the legacy account locator).',
                example: 'acme-analytics',
                normalize: 'lowercase_hyphenate',
            },
        ],
        logoRef: 'snowflake',
        proxyable: true,
        connectionProfiles: [
            {
                // Per-user OAuth with Snowflake as the authorization server (docs/48 §Snowflake):
                // each credential is one role, consented at connect and pinned by the proxy.
                id: 'snowflake_oauth',
                authMode: 'user_delegated',
                mechanism: 'authorization_code',
                credentialKind: 'oauth_client',
                displayName: 'Per-user OAuth (Snowflake OAuth)',
                authorizeUrl: 'https://{account}.snowflakecomputing.com/oauth/authorize',
                signInTestHints: SNOWFLAKE_SIGN_IN_TEST_HINTS,
                tokenUrl: 'https://{account}.snowflakecomputing.com/oauth/token-request',
                scopes: ['refresh_token'],
                clientIdLabel: 'Client ID',
                clientIdPlaceholder: 'OAUTH_CLIENT_ID from SYSTEM$SHOW_OAUTH_CLIENT_SECRETS',
                credentialRole: {
                    label: 'Snowflake role',
                    hint: 'The one role apps bound to this credential run in. Fixed once saved; add another credential for another role.',
                    example: 'ANALYST_RO',
                    blocked: SNOWFLAKE_BLOCKED_ROLES,
                },
                upstreamHeaders: { 'x-snowflake-authorization-token-type': 'OAUTH' },
                setupGuide: {
                    docsLabel: 'Snowflake OAuth docs',
                    stepsTitle: 'Snowflake setup guide (as the signed-in user)',
                    steps: SNOWFLAKE_USER_STEPS,
                    statement: SNOWFLAKE_SECURITY_INTEGRATION_STATEMENT,
                },
                acquisitionDocsUrl: 'https://docs.snowflake.com/en/user-guide/oauth-custom',
                agentInstructions: SNOWFLAKE_AGENT_INSTRUCTIONS,
                acquisitionInstructions: formatAcquisitionGuideSteps(SNOWFLAKE_USER_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'tableau',
        displayName: 'Tableau',
        category: 'analytics',
        baseUrl: 'https://{server}/api',
        placeholders: [
            {
                token: 'server',
                label: 'Server',
                hint: 'The full hostname of your Tableau Server or Cloud site — not rewritten.',
                example: 'tableau.example.com',
            },
        ],
        // PAT sign-in mints a session token via a sign-in call — not standard OAuth2 and
        // not a static header the HTTP proxy can swap — so Tableau is injected-only
        // (ADR-0026, T-3250). proxyable:false ⇒ the picker offers only injected delivery.
        logoRef: 'tableau',
        proxyable: false,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                credentialKind: 'personal_access_token',
                acquisitionDocsUrl: 'https://help.tableau.com/current/server/en-us/security_personal_access_tokens.htm',
                acquisitionInstructions: 'Create a Personal Access Token for a Tableau service account (My Account Settings → Personal Access Tokens) and paste its name and secret.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'axiom',
        displayName: 'Axiom',
        category: 'analytics',
        baseUrl: 'https://{axiom_domain}',
        placeholders: [
            {
                token: 'axiom_domain',
                label: 'Axiom API domain',
                hint: 'Edge host for ingest and query. Use api.axiom.co for dataset and org management APIs.',
                example: 'us-east-1.aws.edge.axiom.co',
            },
        ],
        logoRef: 'axiom',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'service_account',
                authMode: 'service_account',
                authInject: { kind: 'bearer' },
                credentialKind: 'api_key',
                setupGuide: {
                    docsLabel: 'Axiom API token docs',
                    stepsTitle: 'Axiom setup guide',
                    steps: AXIOM_API_TOKEN_STEPS,
                },
                acquisitionDocsUrl: 'https://axiom.co/docs/reference/tokens#api-tokens',
                acquisitionInstructions: formatAcquisitionGuideSteps(AXIOM_API_TOKEN_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        // First-party system integration (ADR-0036): the org user roster, served by
        // the control plane's own directory branch — no upstream, no HTTP base URL,
        // no credential to paste. The secretless `read` credential row is the
        // grant-binding handle; apps call ${GREENLIGHT_PROXY_URL}/<slug>/users with
        // their existing data key. Spec: docs/40-user-directory.md.
        catalogKey: 'greenlight-directory',
        displayName: 'User Directory',
        category: 'platform',
        baseUrl: '',
        logoRef: 'greenlight-directory',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'system',
                authMode: 'system',
                credentialKind: 'none',
                acquisitionDocsUrl: 'https://greenlightbyshift.com/docs/concepts/data-brokering',
                acquisitionInstructions: 'No credential required — Greenlight serves the org roster itself. Registering ' +
                    'creates a grant-binding `read` credential; apps must still request and be granted ' +
                    'access before they can list users, and every call is audited. Each user exposes ' +
                    'only id, email, display name, and active status.',
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        // Same-tenant Azure SQL as a proxied connected database (ADR-0030). The data
        // proxy runs the app's queries server-side and returns rows; the credential
        // never reaches the app. `{data_identity_*}` placeholders in the instructions
        // are filled with this install's managed-identity values by the catalog read
        // service — a DBA grants that identity read access inside the database, then IT
        // registers the server/database here. No HTTP base URL (the SQL branch, not a
        // forward), so proxyable is false and `engine` marks it a database.
        catalogKey: 'azure-sql',
        displayName: 'Azure SQL Database',
        category: 'connected-database',
        baseUrl: '',
        logoRef: 'azure-sql',
        proxyable: false,
        engine: 'azure-sql',
        defaultPort: 1433,
        connectionProfiles: [
            {
                // Passwordless (recommended): the proxy connects as the platform data
                // identity (id-greenlight-data, federated to the control-plane
                // ServiceAccount). No secret is stored or rotated.
                id: 'workload_identity',
                authMode: 'workload_identity',
                credentialKind: 'none',
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/azure/azure-sql/database/authentication-aad-service-principal',
                acquisitionInstructions: 'No secret to paste — Greenlight connects as its managed identity "{data_identity_name}" (client ID {data_identity_client_id}). On each Azure SQL database you want to expose, have a DBA connect as the Entra admin and grant that identity read access:\n\n' +
                    '  CREATE USER [{data_identity_name}] FROM EXTERNAL PROVIDER;\n' +
                    '  ALTER ROLE db_datareader ADD MEMBER [{data_identity_name}];\n\n' +
                    'FROM EXTERNAL PROVIDER requires the logical server to have a server identity with the Directory Readers role (else it fails with Msg 33134). If that prerequisite is not in place, create the user by SID instead — for a managed identity the SID is its client ID, not its object ID:\n\n' +
                    '  CREATE USER [{data_identity_name}] WITH SID = 0x<client-id-as-binary>, TYPE = E;\n' +
                    '  ALTER ROLE db_datareader ADD MEMBER [{data_identity_name}];\n\n' +
                    "Grant a broader role than db_datareader only if apps need to write. Ensure the server firewall allows the control plane's egress address (that one source, not app pods). Then register the logical-server host, port (1433), and database name here.",
                isDefault: true,
                mvpSelectable: true,
            },
            {
                // SQL authentication: a login IT creates on the database; Greenlight vaults
                // the password. A fallback where managed identity isn't usable (e.g. the SP
                // would need to cross tenants, which Azure SQL forbids).
                id: 'sql_password',
                authMode: 'sql_password',
                credentialKind: 'sql_password',
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/azure/azure-sql/database/logins-create-manage',
                acquisitionInstructions: 'Create a contained SQL user with a password on each database and grant it read access:\n\n' +
                    "  CREATE USER [greenlight_reader] WITH PASSWORD = '<strong-password>';\n" +
                    '  ALTER ROLE db_datareader ADD MEMBER [greenlight_reader];\n\n' +
                    "Ensure the server firewall allows the control plane's egress address. Register the host, port (1433), and database here, and paste the username and password — Greenlight vaults the password and never exposes it to apps.",
                isDefault: false,
                mvpSelectable: true,
            },
            {
                // Entra service principal: an app registered in the *database's own* tenant
                // (ADR-0030). The Entra route cross-tenant — a managed identity only
                // authenticates within its own tenant, and Azure SQL forbids cross-tenant SP
                // auth, so the app must be native to the database's tenant (a SQL login is
                // the non-Entra alternative) — and selectable same-tenant when IT prefers a
                // dedicated app identity over the platform's. Greenlight vaults the client
                // secret and reads client_id + tenant_id from auth_config.
                id: 'service_principal',
                authMode: 'service_principal',
                credentialKind: 'service_principal',
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/azure/azure-sql/database/authentication-aad-service-principal',
                acquisitionInstructions: "Use this when the database is in a different Entra tenant than this install (a managed identity can't cross tenants), or whenever you'd rather grant a dedicated Entra application than the platform identity. In the database's tenant, register an Entra application, add a client secret, and note its Application (client) ID and the tenant ID. Then, connected as the Entra admin, grant that app read access inside each database:\n\n" +
                    '  CREATE USER [<app-display-name>] FROM EXTERNAL PROVIDER;\n' +
                    '  ALTER ROLE db_datareader ADD MEMBER [<app-display-name>];\n\n' +
                    "(FROM EXTERNAL PROVIDER needs a Directory-Readers server identity, else Msg 33134 — see the workload-identity note for the SID fallback.) Ensure the server firewall allows the control plane's egress address. Register the host, port (1433), and database here, paste the client ID and tenant ID, and paste the client secret — Greenlight vaults the secret and never exposes it to apps.",
                isDefault: false,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'microsoft-fabric',
        displayName: 'Microsoft Fabric',
        category: 'data-warehouse',
        baseUrl: 'https://api.fabric.microsoft.com',
        searchKeywords: [
            'Power BI',
            'GraphQL',
            'Lakehouse',
            'OneLake',
            'DAX',
            'Semantic model',
            'Warehouse',
            'SQL database',
        ],
        placeholders: [
            {
                token: 'tenant_id',
                label: 'Directory (tenant) ID',
                hint: 'The GUID of the Entra tenant the Fabric app registration lives in, never a domain name or /common.',
                example: '00000000-0000-0000-0000-000000000000',
                format: 'uuid',
            },
        ],
        logoRef: 'microsoft-fabric',
        proxyable: true,
        connectionProfiles: [
            {
                id: 'client_credentials',
                authMode: 'client_credentials',
                credentialKind: 'oauth_client_credentials',
                // Per-tenant Entra token endpoint — IT substitutes {tenant_id} at registration.
                tokenUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token',
                scopes: ['https://api.fabric.microsoft.com/.default'],
                clientIdLabel: 'Application (client) ID',
                setupGuide: {
                    docsLabel: 'Fabric identity docs',
                    stepsTitle: 'Microsoft Fabric setup guide',
                    steps: MICROSOFT_FABRIC_SERVICE_PRINCIPAL_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/rest/api/fabric/articles/identity-support',
                acquisitionInstructions: formatAcquisitionGuideSteps(MICROSOFT_FABRIC_SERVICE_PRINCIPAL_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
            {
                // Per-user OAuth (docs/48 §Microsoft Fabric and Power BI): the REST API and the
                // API for GraphQL as the signed-in user, registered as its own integration beside
                // the service-principal one. The Power BI resource is the token api.fabric accepts.
                id: 'user_delegated',
                authMode: 'user_delegated',
                mechanism: 'authorization_code',
                credentialKind: 'oauth_client',
                displayName: 'Per-user OAuth',
                authorizeUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/authorize',
                signInTestHints: ENTRA_SIGN_IN_TEST_HINTS,
                tokenUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token',
                scopes: [
                    'openid',
                    'profile',
                    'offline_access',
                    'https://analysis.windows.net/powerbi/api/.default',
                ],
                clientIdLabel: 'Application (client) ID',
                clientIdPlaceholder: 'Application (client) ID of the Fabric registration',
                setupGuide: {
                    docsLabel: 'Fabric delegated access docs',
                    stepsTitle: 'Microsoft Fabric setup guide (as the signed-in user)',
                    steps: MICROSOFT_FABRIC_USER_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/rest/api/fabric/articles/identity-support',
                agentInstructions: MICROSOFT_FABRIC_AGENT_INSTRUCTIONS,
                acquisitionInstructions: formatAcquisitionGuideSteps(MICROSOFT_FABRIC_USER_STEPS),
                isDefault: false,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'power-bi-semantic-models',
        displayName: 'Power BI semantic models',
        category: 'analytics',
        baseUrl: 'https://api.powerbi.com',
        searchKeywords: ['Power BI', 'DAX', 'Dataset', 'Semantic model', 'Fabric'],
        placeholders: [
            {
                token: 'tenant_id',
                label: 'Directory (tenant) ID',
                hint: 'The GUID of the Entra tenant the app registration lives in, never a domain name or /common.',
                example: '00000000-0000-0000-0000-000000000000',
                format: 'uuid',
            },
        ],
        logoRef: 'power-bi-semantic-models',
        proxyable: true,
        connectionProfiles: [
            {
                // DAX through executeQueries with the model's row-level security applied to the
                // signed-in user; a second host on the same Power BI resource, so its own entry.
                id: 'user_delegated',
                authMode: 'user_delegated',
                mechanism: 'authorization_code',
                credentialKind: 'oauth_client',
                displayName: 'Per-user OAuth',
                authorizeUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/authorize',
                signInTestHints: ENTRA_SIGN_IN_TEST_HINTS,
                tokenUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token',
                scopes: [
                    'openid',
                    'profile',
                    'offline_access',
                    'https://analysis.windows.net/powerbi/api/.default',
                ],
                clientIdLabel: 'Application (client) ID',
                clientIdPlaceholder: 'Application (client) ID of the registration',
                setupGuide: {
                    docsLabel: 'Execute Queries docs',
                    stepsTitle: 'Power BI semantic models setup guide',
                    steps: POWER_BI_SEMANTIC_MODELS_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/rest/api/power-bi/datasets/execute-queries',
                agentInstructions: POWER_BI_SEMANTIC_MODELS_AGENT_INSTRUCTIONS,
                acquisitionInstructions: formatAcquisitionGuideSteps(POWER_BI_SEMANTIC_MODELS_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
        ],
    },
    {
        catalogKey: 'fabric-warehouse',
        displayName: 'Microsoft Fabric Warehouse',
        category: 'connected-database',
        baseUrl: '',
        placeholders: [
            {
                token: 'tenant_id',
                label: 'Directory (tenant) ID',
                hint: 'The GUID of the Entra tenant the app registration lives in, never a domain name or /common.',
                example: '00000000-0000-0000-0000-000000000000',
                format: 'uuid',
            },
        ],
        logoRef: 'fabric-warehouse',
        proxyable: false,
        engine: 'fabric-warehouse',
        defaultPort: 1433,
        connectionProfiles: [
            {
                // Fabric documents no SQL authentication at all, so unlike azure-sql this
                // entry offers no sql_password fallback — the Entra app is the only way in.
                id: 'service_principal',
                authMode: 'service_principal',
                credentialKind: 'service_principal',
                clientIdLabel: 'Application (client) ID',
                setupGuide: {
                    docsLabel: 'Fabric Warehouse identity docs',
                    stepsTitle: 'Microsoft Fabric Warehouse setup guide',
                    steps: FABRIC_WAREHOUSE_SERVICE_PRINCIPAL_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/fabric/data-warehouse/entra-id-authentication',
                acquisitionInstructions: formatAcquisitionGuideSteps(FABRIC_WAREHOUSE_SERVICE_PRINCIPAL_STEPS),
                isDefault: true,
                mvpSelectable: true,
            },
            {
                // Per-user OAuth (docs/48 §Microsoft Fabric and Power BI): each query runs as
                // the signed-in user over TDS, so the Warehouse's own RLS and grants apply.
                // The Azure SQL resource's scope form keeps its double slash.
                id: 'user_delegated',
                authMode: 'user_delegated',
                mechanism: 'authorization_code',
                credentialKind: 'oauth_client',
                displayName: 'Per-user OAuth',
                authorizeUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/authorize',
                signInTestHints: ENTRA_SIGN_IN_TEST_HINTS,
                tokenUrl: 'https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token',
                scopes: ['openid', 'profile', 'offline_access', 'https://database.windows.net//.default'],
                clientIdLabel: 'Application (client) ID',
                clientIdPlaceholder: 'Application (client) ID of the Fabric registration',
                setupGuide: {
                    docsLabel: 'Fabric Warehouse identity docs',
                    stepsTitle: 'Microsoft Fabric Warehouse setup guide (as the signed-in user)',
                    steps: FABRIC_WAREHOUSE_USER_STEPS,
                },
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/fabric/data-warehouse/entra-id-authentication',
                agentInstructions: FABRIC_WAREHOUSE_USER_AGENT_INSTRUCTIONS,
                acquisitionInstructions: formatAcquisitionGuideSteps(FABRIC_WAREHOUSE_USER_STEPS),
                isDefault: false,
                mvpSelectable: true,
            },
            {
                // Passwordless via the platform data identity. Catalogued, not selectable:
                // a managed identity cannot cross tenants, it still has to sit in the Fabric
                // tenant setting's security group, and the TDS token audience a Fabric
                // endpoint accepts is unverified against a live tenant.
                id: 'workload_identity',
                authMode: 'workload_identity',
                credentialKind: 'none',
                acquisitionDocsUrl: 'https://learn.microsoft.com/en-us/fabric/data-warehouse/entra-id-authentication',
                acquisitionInstructions: 'Not yet selectable. Connecting as Greenlight\'s own managed identity "{data_identity_name}" (client ID {data_identity_client_id}) needs two things confirmed against a live Fabric tenant first: that a user-assigned managed identity can be added to the security group the "Service principals can use Fabric APIs" tenant setting is scoped to, and which TDS token audience a Fabric SQL endpoint accepts. Until then, register the service-principal profile — its token is minted by the driver, so Greenlight chooses no audience of its own.',
                isDefault: false,
                mvpSelectable: false,
            },
        ],
    },
];
//# sourceMappingURL=seed.js.map